import { sql } from 'drizzle-orm';
import {
  pgTable,
  pgPolicy,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  uuid,
  bigint,
} from "drizzle-orm/pg-core";
import { organizations } from './organizations';
import { users } from './users';

/** queued and running are active: a firm has at most one active export (uq_firm_export_jobs_one_active_per_org). */
export const FIRM_EXPORT_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type FirmExportStatus = typeof FIRM_EXPORT_STATUSES[number];

// Full data export of one firm (firm admin, async). The ZIP is in object storage at storage_key
// and can be downloaded until expires_at.
export const firmExportJobs = pgTable("firm_export_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  requestedBy: varchar("requested_by").references(() => users.id, { onDelete: "set null" }),
  status: varchar("status", { length: 20 }).$type<FirmExportStatus>().notNull().default("queued"),
  storageKey: text("storage_key"),
  manifest: jsonb("manifest"), // table row counts and file counts; the full manifest.json is in the ZIP
  sizeBytes: bigint("size_bytes", { mode: "number" }),
  error: text("error"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  startedAt: timestamp("started_at"),
  heartbeatAt: timestamp("heartbeat_at"), // bumped while the job runs; a stale running job no longer blocks the firm
  completedAt: timestamp("completed_at"),
  expiresAt: timestamp("expires_at"), // no download link is issued after this
}, (table) => [
  index("idx_firm_export_jobs_org_id").on(table.orgId),
  uniqueIndex("uq_firm_export_jobs_one_active_per_org").on(table.orgId).where(sql`status in ('queued', 'running')`),
  pgPolicy('firm_export_jobs_tenant_isolation', {
    as: 'permissive',
    for: 'all',
    to: 'app_user',
    using: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
    withCheck: sql`org_id = current_setting('app.current_tenant', true)::uuid`,
  }),
]).enableRLS();

export type FirmExportJob = typeof firmExportJobs.$inferSelect;
export type InsertFirmExportJob = typeof firmExportJobs.$inferInsert;
